import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Flex, Segmented, Tag } from 'antd';
import { IconMapPins } from '@tabler/icons-react';
import { BetaSchemaForm, FormListActionType, ProFormColumnsType } from '@ant-design/pro-components';
import { PlusOutlined } from '@ant-design/icons';
import { CrudMode } from '@ohpen/enso-ui';

import { FirmGetById } from '@pmp/api';
import { TabProps } from '../interfaces';
import { CrudFormCard } from './CrudFormCard';
import { AddressField } from './AddressField';
import styles from '../styles.module.css';

export const TradingNames: React.FC<TabProps> = ({ form, mode, selectsOptions, fcaModifiedFields, removeFcaModifiedField }) => {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<string>('Active');
  const tradingAddressesRef = useRef<FormListActionType<any>>(null);
  const initialStatusByIdRef = useRef<Record<string, boolean>>({});
  const readonly = mode === CrudMode.VIEW;

  // Seed initial status map from form's initial values (persisted rows only)
  useEffect(() => {
    if (Object.keys(initialStatusByIdRef.current).length > 0) return;
    const list = form?.getFieldValue?.('tradingNames') ?? form?.getFieldValue?.(['tradingNames']);
    if (Array.isArray(list)) {
      const map: Record<string, boolean> = {};
      list.forEach((item: any) => {
        if (item?.id != null) {
          map[item.id] = !!item.isActive;
        }
      });
      initialStatusByIdRef.current = map;
    }
  }, [form]);

  const columns: ProFormColumnsType<FirmGetById, 'text'>[] = useMemo(
    () => [
      {
        dataIndex: 'tradingNames',
        valueType: 'formList',
        fieldProps: {
          creatorButtonProps: false,
          actionRef: tradingAddressesRef,
          copyIconProps: false,
          deleteIconProps: { tooltipText: 'Remove' },
          alwaysShowItemLabel: true,
          // Filter visibility only; do not mutate form data.
          itemRender: (doms: { listDom: React.ReactNode; action: React.ReactNode }, listMeta: { index: number }) => {
            const index = listMeta?.index ?? 0;
            const list = form?.getFieldValue?.('tradingNames') ?? [];
            const row = Array.isArray(list) ? list[index] : form?.getFieldValue?.(['tradingNames', index]);
            const isActive = row?.isActive !== false; // treat undefined as active
            const show = filter === 'All' || (filter === 'Active' ? isActive : !isActive);
            return show ? (
              <div className={styles.tradingName}>
                {doms.listDom}
                {doms.action}
              </div>
            ) : (
              <span data-trading-name-hidden />
            );
          },
          // Only allow deleting items that are not yet persisted (no id)
          actionRender: (field: any, _action: any, defaultActionDom: any[]) => {
            const index = field?.name as number;
            const list = form?.getFieldValue?.('tradingNames') ?? [];
            const row = Array.isArray(list) ? list[index] : form?.getFieldValue?.(['tradingNames', index]);
            const isPersisted = !!row?.id;
            // If persisted, hide delete action entirely; otherwise show default actions
            return isPersisted ? [] : defaultActionDom;
          },
          actionGuard: {
            beforeRemoveRow: async (index: number) => {
              const list = form?.getFieldValue?.('tradingNames') ?? [];
              const row = Array.isArray(list) ? list[index] : form?.getFieldValue?.(['tradingNames', index]);
              // Block removal when the row has an id (already saved on server)
              return !row?.id;
            },
          },
        },
        columns: [
          {
            valueType: 'group',
            colProps: { span: 24 },
            columns: [
              {
                title: t('lender.firms.labels.name'),
                dataIndex: 'name',
                valueType: 'text',
                readonly,
                colProps: { span: 12 },
                formItemProps: {
                  style: { marginBottom: 16 },
                  rules: [{ required: true, message: 'Trading name is required' }],
                },
              },
              {
                valueType: 'dependency',
                name: ['isUsed'],
                columns: ({ isUsed }) => [
                  {
                    title: t('lender.firms.labels.status'),
                    dataIndex: 'isActive',
                    valueType: 'switch',
                    readonly,
                    colProps: { span: 12 },
                    formItemProps: {
                      tooltip: isUsed && !readonly ? t('lender.firms.labels.tradingNameDeactivateTooltip') : undefined,
                    },
                    fieldProps: readonly
                      ? undefined
                      : {
                          checkedChildren: t('lender.firms.labels.active'),
                          unCheckedChildren: t('lender.firms.labels.inactive') ?? t('lender.firms.labels.inactive'),
                          disabled: !!isUsed,
                        },
                    // TODO: Use renderSwitchToTag from Enso UI when available
                    // render:  readonly ? undefined : renderSwitchToTag,
                    render: (dom, entity) => {
                      if (!readonly) return dom;
                      const active = !!(entity as any)?.value;

                      return (
                        <Tag color={active ? 'green' : 'red'}>
                          {active
                            ? t('lender.firms.labels.active')
                            : (t('lender.firms.labels.inactive') ?? t('lender.firms.labels.inactive'))}
                        </Tag>
                      );
                    },
                  },
                ],
              },
              {
                dataIndex: 'isUsed',
                hideInForm: true,
              },
            ],
          },
          {
            valueType: 'group',
            colProps: { span: 24 },
            columns: [
              {
                title: t('lender.firms.labels.businessDevelopmentManager'),
                dataIndex: 'bdm',
                valueType: 'text',
                readonly,
                colProps: { span: 12 },
                formItemProps: {
                  rules: [{ required: true, message: 'Required' }],
                  style: { marginBottom: 16 },
                },
              },
            ],
          },
          {
            dataIndex: 'city',
            valueType: 'text' as const,
            renderFormItem: (schema, config) => {
              const index = Number((config as any)?.id?.split('_')[1]);
              const row = form?.getFieldValue(['tradingNames', index]);
              return (
                <AddressField
                  readonly={readonly}
                  form={form}
                  getAddress={() => form?.getFieldValue(['tradingNames', index])}
                  fcaModifiedFields={fcaModifiedFields}
                  removeFcaModifiedField={removeFcaModifiedField}
                  isFromFca={row?.isActive === true}
                />
              );
            },
            colProps: { span: 24 },
          },
        ],
      },
    ],
    [t, readonly, form, filter, fcaModifiedFields, removeFcaModifiedField],
  );

  const onCreate = useCallback(() => {
    tradingAddressesRef.current?.add({ isActive: true });
  }, []);

  return (
    <CrudFormCard
      icon={<IconMapPins />}
      title={t('lender.firms.labels.tradingNames')}
      extra={
        <Flex gap="8px" justify="space-between" className={styles.tradingNamesExtra}>
          <Segmented value={filter} options={['Active', 'Inactive', 'All']} onChange={setFilter} size="small" />
          {!readonly && filter !== 'Inactive' && (
            <Button icon={<PlusOutlined />} onClick={onCreate} size="small">
              {t('global.buttons.add')}
            </Button>
          )}
        </Flex>
      }
      bodyScroll
    >
      <BetaSchemaForm<FirmGetById> form={form} layoutType="Embed" columns={columns} submitter={false} />
    </CrudFormCard>
  );
};
